import { ensureDir, testAccess } from '@/utils/fs/fs-extra.utils';
import { ElectronApi } from './electron.api';
import { Shape } from './shape';
import { useImagesStore } from '@/store/use-images-store';
import { WatchfaceService } from './watchface';
import generateID from '@/utils/generate-id';
import JSZip from 'jszip';
import saveAs from 'file-saver';
import { zipDir } from '@/utils/fs/zip.utils';
import { textToMd5 } from '@/utils/md5.utils';
import { cloneDeep } from 'lodash-es';
import { arraybufferToBase64, getImageSize } from '@/utils/fs/image.utils';

export type WatchfaceTemplateType = (typeof TemplateService)['types'][number];

export interface WatchfaceTemplateItem {
  id: string;
  name: string;
  type: WatchfaceTemplateType;
  deviceType: string;
  /**
   * 模版预览图：为base64格式
   */
  preview: string;
  // 相对路径
  templateZip: string;

  /**
   * 最近更新时间
   */
  createdAt: number;
  updatedAt: number;
}

/**
 * 表盘模版库
 */
export class TemplateService {
  private static layers = 'layers.json';
  private static resources = 'resources';
  private static configData = 'template.config.json';

  /**
   * 模版类型
   *
   * @static
   * @memberof TemplateService
   */
  static types = ['时间日期', '背景刻度', '组件', '其他'] as const;
  async templateDir() {
    return ElectronApi.path?.join(
      (await ElectronApi.getPath('userData')) ?? '',
      'Templates'
    );
  }
  async templateFile() {
    const _templateFile = TemplateService.configData;
    return ElectronApi.path?.join(
      (await this.templateDir()) ?? '',
      _templateFile
    );
  }

  /**
   * 读取模版数据
   * @returns
   */
  async readTemplates() {
    const fs = ElectronApi.fs;
    if (!fs) return;
    try {
      const filename = await this.templateFile();
      if (!filename) return;
      if ((await testAccess(filename)) == false) {
        await fs.promises.writeFile(filename, JSON.stringify([]));
      }
      const res = await fs.promises
        .readFile(filename, 'utf8')
        .then((r) => JSON.parse(r));
      return res as WatchfaceTemplateItem[];
    } catch (error) {
      console.log(error);
    }
  }

  private async resetTemplates(templates: WatchfaceTemplateItem[]) {
    const fs = ElectronApi.fs;
    const filename = await this.templateFile();
    if (!fs || !filename) return;

    await fs.promises.writeFile(
      filename,
      JSON.stringify(templates, undefined, 2)
    );
  }

  private async writeTemplate(
    template?: WatchfaceTemplateItem,
    deleteId?: string
  ) {
    const fs = ElectronApi.fs;
    const templateDir = await this.templateDir();
    if (!fs || !templateDir) return;

    let templates = await this.readTemplates();
    if (!templates) templates = [];
    // 添加模版
    if (template) {
      const index = templates.findIndex((item) => item.id == template.id);
      if (index > -1) {
        templates[index] = { ...templates[index], ...template };
      } else {
        templates.push(template);
      }
    }

    // 删除模版
    if (deleteId) {
      const tempalte = templates.find((item) => item.id == deleteId);
      const _templateDir = tempalte?.templateZip;
      templates = templates.filter((item) => item.id !== deleteId);
      if (_templateDir && _templateDir.startsWith(templateDir)) {
        await fs.promises.rm(_templateDir, { recursive: true, force: true });
      }
    }

    await this.resetTemplates(templates);
  }

  /**
   * 新建模版数据
   * @param data
   * @param shapeIds
   * @param overwriteIfNameExists 当名称存在时，是否覆盖
   * @returns
   */
  async createTemplate(
    data: Omit<
      WatchfaceTemplateItem,
      'templateZip' | 'id' | 'createdAt' | 'updatedAt'
    >,
    shapeData: {
      shapeIds: string[];
      colorId: string;
    },
    overwriteIfNameExists?: boolean
  ) {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    const templateDir = await this.templateDir();
    if (!fs || !path || !templateDir) return;
    const { shapeIds, colorId } = shapeData;
    // 判断是否存在
    if (overwriteIfNameExists) {
      const name = data.name;
      const templates = await this.readTemplates();
      // 查找已存在的模版，并删除
      const id = templates?.find((item) => item.name == name)?.id;
      if (id) {
        await this.deleteTemplate(id);
      }
    }

    const id = generateID();

    const _templateDir = path.join(templateDir, id);
    await ensureDir(_templateDir);

    // 写入layers
    const layers: StoreLayerItem[] = [];
    shapeIds.forEach((shapeId) => {
      const shape = Shape.getShape(shapeId);
      const layer = shape?.layer;
      if (layer) layers.push(cloneDeep(layer));
    });
    if (!layers.length) return;

    // 处理layers，仅保留一个配色的资源
    const _layers = [...layers];
    while (_layers.length) {
      const cur = _layers.shift();
      if (cur?.children?.length) _layers.push(...cur.children);
      const names = Object.keys(cur?.attrs.resources ?? {});
      names.forEach((name) => {
        const colorIds = Object.keys(cur?.attrs.resources?.[name] ?? {});
        colorIds.forEach((_colorId) => {
          if (_colorId !== colorId) {
            delete cur?.attrs.resources?.[name][_colorId];
          }
        });
      });
    }

    const layersFile = path.join(_templateDir, TemplateService.layers);
    await fs.promises.writeFile(
      layersFile,
      JSON.stringify(layers, undefined, 2),
      'utf8'
    );
    // 写入resources
    const resourcesDir = path.join(_templateDir, TemplateService.resources);
    if (!resourcesDir) return;
    await ensureDir(resourcesDir);
    const images = useImagesStore().getUsingImages(layers);
    await new WatchfaceService().resource.writeImages(images, resourcesDir);

    // 将资源打包，然后删除原来的目录
    const _templateZipAb = (await zipDir(_templateDir)) as ArrayBuffer;
    if (!_templateZipAb) return;
    const _templateZipMd5 = textToMd5(_templateZipAb, true) + '.zip';
    const _templateZipName = path.join(templateDir, _templateZipMd5);
    await fs.promises.writeFile(_templateZipName, Buffer.from(_templateZipAb));
    await fs.promises.rm(_templateDir, { recursive: true, force: true });

    const tempalte: WatchfaceTemplateItem = {
      ...data,
      templateZip: path.basename(_templateZipName),
      id,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    await this.writeTemplate(tempalte);
  }

  async deleteTemplate(id: string) {
    await this.writeTemplate(undefined, id);
  }

  async updateTemplate(
    id: string,
    data: Partial<Omit<WatchfaceTemplateItem, 'templateZip' | 'id'>>
  ) {
    const templates = await this.readTemplates();
    if (!templates) return;
    const tempalte = templates.find((item) => item.id == id);
    if (!tempalte) return;
    const { name, type, deviceType, preview } = data;
    if (name) tempalte.name = name;
    if (type) tempalte.type = type;
    if (preview) tempalte.preview = preview;
    if (deviceType) tempalte.deviceType = deviceType;
    tempalte.updatedAt = Date.now();
    await this.writeTemplate(tempalte);
  }

  /**
   * 判断模版名称是否存在
   * @param name 模版名称
   * @returns
   */
  async testExistsName(name: string) {
    const templates = await this.readTemplates();
    return !!templates?.find((item) => item.name == name);
  }

  /**
   * 导出模版
   */
  async exportTemplates(ids: string[]) {
    try {
      const fs = ElectronApi.fs;
      const path = ElectronApi.path;
      const templateDir = await this.templateDir();
      if (!fs || !path || !templateDir) return;

      // 查询要导出的模版
      const templates = await this.readTemplates().then((r) =>
        r?.filter((item) => ids.includes(item.id))
      );
      if (!templates?.length) return;

      const zip = new JSZip();
      zip.file(
        TemplateService.configData,
        JSON.stringify(templates, undefined, 2)
      );

      for (const template of templates) {
        const _templateZip = template.templateZip;
        if (!_templateZip) continue;
        const _templateZipFile = path.join(templateDir, _templateZip);
        if ((await testAccess(_templateZipFile)) !== true) continue;
        const content = await fs.promises.readFile(_templateZipFile);
        zip.file(_templateZip, Buffer.from(content));
      }

      // 下载
      await zip.generateAsync({ type: 'blob' }).then(function (content) {
        // see FileSaver.js
        saveAs(content, ['表盘模版'].join('-') + '.zip');
      });
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * 导入模版
   */
  async importTemplates(zipFile: string) {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    const templateDir = await this.templateDir();
    if (!fs || !path || !templateDir) return;

    const fileBf = await fs.promises.readFile(zipFile);
    const zip = await JSZip.loadAsync(fileBf);
    for (const item of Object.entries(zip.files)) {
      const [file, fileObj] = item;
      if (file == TemplateService.configData) {
        const content = await fileObj.async('string');
        try {
          const _templates = JSON.parse(content) as WatchfaceTemplateItem[];
          const templates = (await this.readTemplates()) ?? [];
          _templates.forEach((item) => {
            // 重置特殊的字段
            item.id = generateID();
            item.createdAt = Date.now();
            item.updatedAt = Date.now();
          });
          templates?.push(..._templates);
          await this.resetTemplates(templates);
        } catch (error) {
          console.log(error);
        }
      }
      if (file.endsWith('.zip')) {
        const content = await fileObj.async('arraybuffer');
        await fs.promises.writeFile(
          path.join(templateDir, file),
          Buffer.from(content)
        );
      }
    }
  }

  /**
   * 解析模版，或模版具体数据：layers和资源
   * @param templateId 模版id
   */
  async parseTemplateZip(templateId: string) {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    const templateDir = await this.templateDir();
    if (!fs || !path || !templateDir) return;

    // 获取模板zip文件
    const templates = await this.readTemplates();
    const tempalte = templates?.find((item) => item.id == templateId);
    if (!tempalte || !templateDir) return;
    const templateZip = path.join(templateDir, tempalte.templateZip);
    if ((await testAccess(templateZip)) != true) return;

    // 读取zip文件
    const fileBf = await fs.promises.readFile(templateZip);
    const zip = await JSZip.loadAsync(fileBf);
    const layers: StoreLayerItem[] = [];
    const resources: StoreResourceItem[] = [];

    for (const item of Object.entries(zip.files)) {
      const [filename, fileObj] = item;
      // 过滤纯目录项
      if (fileObj.dir) continue;
      if (filename == TemplateService.layers) {
        const content = await fileObj.async('string');
        try {
          layers.push(...JSON.parse(content));
        } catch (error) {
          console.log(error);
        }
      }

      const resourcePrifix = TemplateService.resources + '/';
      if (filename.startsWith(TemplateService.resources)) {
        const content = await fileObj.async('arraybuffer');
        const base64 = await arraybufferToBase64(content, filename);
        const { width = 0, height = 0 } =
          (await getImageSize(undefined, base64)) ?? {};
        resources.push({
          realpath: filename,
          // 将basename去resource/即可
          basename: filename.replace(resourcePrifix, ''),
          // 即base64
          value: base64,
          width,
          height
        });
      }
    }

    const res = {
      layers,
      resources
    };

    return res;
  }
}
